import './_styles.scss';
import './_styles.dark-mode.scss';
import React, { useEffect, useState, useRef, Fragment } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Panel, Button, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { toNumber } from 'lodash';
import qs from 'qs';

import { useDispatch } from 'react-redux';
import Tooltip from '../../../components/Tooltip';
import Alert from '../../../components/Alert';
import NewYear from './components/NewYear';
import FavLogoFiles from './components/Files/FavLogo';
import CustomFiles from './components/Files/Custom';
import Loader from '../../../components/Loaders/Loader';

import {
  getGeneralList,
  saveGeneral,
  uploadLogo,
  uploadFavicon,
  uploadRobots,
  uploadSitemap,
  removeLogo,
  removeFavicon,
  removeSitemap,
  removeRobots,
} from '../../../services/endpoints/settings';
import { getNavbarConfigAction } from '../../../redux/actions/header';
import { getSettingsConfigAction } from '../../../redux/actions/settings';
import { newYearActivate } from '../../../services/endpoints/newyear';
import { pageWrapperScrollTop, toBase64 } from '../../../utils/helper';
import { fieldKeys } from './helpers';

const General = () => {
  const dispatch = useDispatch();

  const formRef = useRef(null);

  const location = useLocation();
  const history = useHistory();

  const queryString = qs.parse(location.search.slice(1));

  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState({
    page: true,
    submit: false,
    new_year: false,
    logo: false,
    favicon: false,
    site_map: false,
    robots: false,
  });
  const [error, setError] = useState('');

  const fetchList = async (loader = true, field) => {
    loader && setIsLoading((prevState) => ({ ...prevState, page: true }));
    try {
      const response = await getGeneralList(queryString);
      if (field) {
        setData((prevState) => ({ ...prevState, [field]: response.data?.[field] }));
      } else {
        setData(response.data);
      }

      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
    } catch (error) {
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const onToggleSnow = async () => {
    if (!data.new_year.current) {
      setIsLoading((prevState) => ({ ...prevState, new_year: true }));
      await newYearActivate();
    }
    history.push('/new-year');
  };

  const detetectShowHR = (key) => {
    const detectCPF = data?.cpf_mode ? 'cpf_mode' : 'minimum_drip_feed_interval';
    return (
      key === 'favorite_services'
      || key === 'ticket_per_user'
      || key === 'minimum_drip_feed_interval'
      || key === detectCPF
      || key === 'google_verification_code'
      || key === 'custom_footer'
    );
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading((prevState) => ({ ...prevState, submit: true }));

    const form = formRef.current;
    const payload = {};
    fieldKeys?.forEach((key) => {
      if (form?.[key]) {
        payload[key] = toNumber(form[key]?.value) || form[key]?.value === '0'
          ? toNumber(form[key]?.value)
          : form[key]?.value === 'true'
            ? true
            : form[key]?.value === 'false'
              ? false
              : form[key]?.value;
      }
    });

    try {
      await saveGeneral(payload);
      setIsLoading((prevState) => ({ ...prevState, submit: false }));
      dispatch(getSettingsConfigAction(queryString));
      dispatch(getNavbarConfigAction());
    } catch (error) {
      setError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, submit: false }));
    } finally {
      pageWrapperScrollTop();
    }
  };

  const onUploadFile = async (type, event) => {
    const file = event.target?.files?.[0];
    let request = null;
    switch (type) {
    case 'logo':
      request = uploadLogo;
      break;

    case 'favicon':
      request = uploadFavicon;
      break;

    case 'site_map':
      request = uploadSitemap;
      break;

    case 'robots':
      request = uploadRobots;
      break;

    default:
      break;
    }
    if (!request) return;

    if (type === 'logo' || type === 'favicon') {
      const base64File = await toBase64(file);
      setData((prevState) => ({
        ...prevState,
        [type]: {
          ...prevState[type],
          current: base64File,
        },
      }));
    }

    const formKey = type === 'logo'
      ? 'logo_file'
      : type === 'favicon'
        ? 'favicon_file'
        : type === 'site_map'
          ? 'site_map_file'
          : type === 'robots'
            ? 'robots_file'
            : '';
    const formData = new FormData();
    formData.append(formKey, file);

    setIsLoading((prevState) => ({ ...prevState, [type]: true }));
    try {
      await request(formData);
      if (type === 'site_map' || type === 'robots') {
        await fetchList(false, type);
      }
      setIsLoading((prevState) => ({ ...prevState, [type]: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, [type]: false }));
      setError(error?.data?.error_message ?? 'Something went wrong');
      pageWrapperScrollTop();
    } finally {
      event.target.value = null;
    }
  };

  const onChangeFileInput = (type, event) => {
    event.persist();
    if (event.target?.files?.length) {
      onUploadFile(type, event);
    }
  };

  const onRemoveFile = async (type) => {
    let request = null;
    switch (type) {
    case 'logo':
      request = removeLogo;
      break;

    case 'favicon':
      request = removeFavicon;
      break;

    case 'site_map':
      request = removeSitemap;
      break;

    case 'robots':
      request = removeRobots;
      break;

    default:
      break;
    }
    if (!request) return;

    setError('');
    setIsLoading((prevState) => ({ ...prevState, [type]: true }));
    try {
      await request();
      if (type === 'logo' || type === 'favicon') {
        setData((prevState) => ({
          ...prevState,
          [type]: {
            ...prevState[type],
            current: null,
          },
        }));
      } else {
        await fetchList(false, type);
      }
      setIsLoading((prevState) => ({ ...prevState, [type]: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, [type]: false }));
      setError(error?.data?.error_message ?? 'Something went wrong');
      pageWrapperScrollTop();
    }
  };

  useEffect(() => {
    fetchList();
    document.title = 'General';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isLoading.page ? (
        <div className="loader-container" style={{ minHeight: '300px' }}>
          <Loader />
        </div>
      ) : (
        <Panel className="settings-general">
          {isLoading.submit ? <div className="modal-loader" /> : null}
          <Panel.Body>
            {data?.new_year ? <NewYear isLoading={isLoading.new_year} data={data?.new_year} onToggleSnow={onToggleSnow} /> : null}

            {error && <Alert text={error} />}

            <FavLogoFiles
              isLoading={isLoading}
              logo={data?.logo}
              favicon={data?.favicon}
              onRemoveFile={onRemoveFile}
              onChangeFileInput={onChangeFileInput}
            />

            <form onSubmit={onSubmit} ref={formRef}>
              {fieldKeys?.map((key) => {
                const field = data?.[key];
                if (!field) return null;

                const list = field?.type === 'bool'
                  ? [
                    { id: true, title: 'Enabled' },
                    { id: false, title: 'Disabled' },
                  ]
                  : field?.list;
                return (
                  <Fragment key={key}>
                    <FormGroup>
                      <ControlLabel htmlFor={key}>
                        {field?.title}
                        {' '}
                        <Tooltip placement="top" text={field?.tooltip} tooltipClass="tooltip__top">
                          {field?.tooltip ? <span className="fas fa-info-circle cursor-help" /> : null}
                        </Tooltip>
                      </ControlLabel>
                      {field?.type === 'text' ? (
                        <FormControl type={field?.type} id={key} defaultValue={field?.current} name={key} />
                      ) : field?.type === 'textarea' ? (
                        <FormControl
                          componentClass={field?.type}
                          rows={7}
                          id={key}
                          placeholder={
                            key === 'custom_header'
                              ? '<style type="text/css">...</style>'
                              : key === 'custom_footer'
                                ? '<script>...</script>'
                                : undefined
                          }
                          defaultValue={field?.current}
                          name={key}
                        />
                      ) : (
                        <FormControl componentClass="select" id={key} defaultValue={field?.current} name={key}>
                          {list?.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.title}
                            </option>
                          ))}
                        </FormControl>
                      )}
                    </FormGroup>
                    {detetectShowHR(key) ? <hr key={key} /> : null}
                  </Fragment>
                );
              })}

              <CustomFiles
                isLoading={isLoading}
                site_map={data?.site_map}
                robots={data?.robots}
                onRemoveFile={onRemoveFile}
                onChangeFileInput={onChangeFileInput}
              />

              <hr />

              <Button bsStyle="primary" type="submit" disabled={isLoading.submit}>
                Save changes
              </Button>
            </form>
          </Panel.Body>
        </Panel>
      )}
    </>
  );
};

export default General;
